import type { LayerSpecification } from "@vietmap/vietmap-gl-js/dist/vietmap-gl";

/**
 * Map themes for the Simple-mode "Bảng màu" picker, modelled on well-known
 * basemaps (Google Maps, Carto, OpenStreetMap…).
 *
 * A theme is not a handful of tints on top of whatever style is open: applying
 * one reloads a Vietmap base style — `vietmap-street` for light themes,
 * `vietmap-dark` for dark ones — and recolours every land, water, road, label
 * and boundary layer from `colors` (see `applyPaletteTheme`). Icons/POI
 * colours stay as Vietmap tuned them for that base.
 */

type RoadTier = {
  /** Road fill (`line-color` of the road / bridge / tunnel layer). */
  fill: string;
  /** Outline (`*_casing` layers). */
  casing: string;
};

type LabelColors = { text: string; halo: string };

export type PaletteColors = {
  land: string;
  water: string;
  wood: string;
  park: string;
  grass: string;
  residential: string;
  cemetery: string;
  hospital: string;
  school: string;
  aeroway: string;
  building: string;
  buildingOutline: string;
  roads: {
    motorway: RoadTier;
    trunk: RoadTier;
    primary: RoadTier;
    secondary: RoadTier;
    tertiary: RoadTier;
    minor: RoadTier;
    path: RoadTier;
  };
  rail: string;
  boundary: string;
  labels: {
    place: LabelColors;
    road: LabelColors;
    water: LabelColors;
    park: LabelColors;
    boundary: LabelColors;
  };
};

export type ColorPalette = {
  id: string;
  /** Display name (i18n key). */
  name: string;
  /** One-line description (i18n key). */
  description: string;
  tone: "light" | "dark";
  /** Vietmap style the theme starts from. */
  base: "vietmap-street" | "vietmap-dark";
  /** Recolouring; omitted for "Mặc định" = the base style untouched. */
  colors?: PaletteColors;
  /** Chip stripes when there is no `colors` to derive them from. */
  swatch?: string[];
};

const LIGHT = "vietmap-street" as const;
const DARK = "vietmap-dark" as const;

/** Curated themes. Order = chip order: light first, then dark. */
export const COLOR_PALETTES: ColorPalette[] = [
  // Vietmap's own street style, unmodified — the way back after trying themes.
  {
    id: "default",
    name: "Mặc định",
    description: "Style Đường phố gốc của Vietmap",
    tone: "light",
    base: LIGHT,
    swatch: ["#f6f1e4", "#c3c6d1", "#c0f0a8", "#ff9330", "#90d5ef"],
  },

  // Google Maps (2024) — cool neutral land, mint parks, bright blue water,
  // white streets, soft yellow highways.
  {
    id: "classic",
    name: "Classic",
    description: "Phong cách Google Maps — sáng, rõ ràng",
    tone: "light",
    base: LIGHT,
    colors: {
      land: "#f5f3ef",
      water: "#9ecbf2",
      wood: "#b5dbae",
      park: "#c2e8c0",
      grass: "#d3edcd",
      residential: "#eeece8",
      cemetery: "#d8e8d3",
      hospital: "#fbe4e4",
      school: "#f4ecdc",
      aeroway: "#e6e8ee",
      building: "#e8e6e2",
      buildingOutline: "#d9d6d0",
      roads: {
        motorway: { fill: "#fcd77f", casing: "#e8b44c" },
        trunk: { fill: "#fde9a8", casing: "#e9c36d" },
        primary: { fill: "#ffffff", casing: "#c9c6c0" },
        secondary: { fill: "#ffffff", casing: "#cfccc6" },
        tertiary: { fill: "#ffffff", casing: "#d6d3ce" },
        minor: { fill: "#ffffff", casing: "#dcd9d4" },
        path: { fill: "#ffffff", casing: "#e0ddd8" },
      },
      rail: "#b9bcc2",
      boundary: "#9a9ca3",
      labels: {
        place: { text: "#3c4043", halo: "#ffffff" },
        road: { text: "#5f6368", halo: "#ffffff" },
        water: { text: "#3f7fb8", halo: "#e3f1fc" },
        park: { text: "#2d7a3f", halo: "#ffffff" },
        boundary: { text: "#70757a", halo: "#ffffff" },
      },
    },
  },

  // Carto Voyager — warm cream land, sage greens, muted teal water, amber
  // highways.
  {
    id: "voyager",
    name: "Voyager",
    description: "Phong cách Carto Voyager — ấm, dịu mắt",
    tone: "light",
    base: LIGHT,
    colors: {
      land: "#f8f4ee",
      water: "#acd0e4",
      wood: "#cfe1bd",
      park: "#d6e8c6",
      grass: "#dfeccf",
      residential: "#f0ebe3",
      cemetery: "#dde6d0",
      hospital: "#f7e3df",
      school: "#f3ead8",
      aeroway: "#ebe7e1",
      building: "#e9e2d8",
      buildingOutline: "#dcd3c6",
      roads: {
        motorway: { fill: "#f6c37c", casing: "#e3a757" },
        trunk: { fill: "#fadca5", casing: "#e8c086" },
        primary: { fill: "#fdf0d4", casing: "#e3cfae" },
        secondary: { fill: "#ffffff", casing: "#e1d6c6" },
        tertiary: { fill: "#ffffff", casing: "#e4dbce" },
        minor: { fill: "#ffffff", casing: "#e8e1d6" },
        path: { fill: "#fbf8f3", casing: "#e8e1d6" },
      },
      rail: "#c4bdb2",
      boundary: "#a59f96",
      labels: {
        place: { text: "#4a4540", halo: "#fbf8f3" },
        road: { text: "#6e6860", halo: "#ffffff" },
        water: { text: "#4f84a8", halo: "#e6f0f6" },
        park: { text: "#567a45", halo: "#f8f4ee" },
        boundary: { text: "#857f76", halo: "#f8f4ee" },
      },
    },
  },

  // Carto Positron — near-white, desaturated; the go-to canvas for data
  // overlays.
  {
    id: "positron",
    name: "Positron",
    description: "Phong cách Carto Positron — tối giản, làm nền cho dữ liệu",
    tone: "light",
    base: LIGHT,
    colors: {
      land: "#fafaf8",
      water: "#d4dde2",
      wood: "#e2eadb",
      park: "#e5ede0",
      grass: "#eaf0e5",
      residential: "#f3f3f1",
      cemetery: "#e8ede5",
      hospital: "#f5eded",
      school: "#f4f1ea",
      aeroway: "#efefee",
      building: "#ececea",
      buildingOutline: "#e1e1df",
      roads: {
        motorway: { fill: "#ffffff", casing: "#bdbdbb" },
        trunk: { fill: "#ffffff", casing: "#c6c6c4" },
        primary: { fill: "#ffffff", casing: "#d0d0ce" },
        secondary: { fill: "#ffffff", casing: "#dededc" },
        tertiary: { fill: "#ffffff", casing: "#e2e2e0" },
        minor: { fill: "#ffffff", casing: "#e6e6e4" },
        path: { fill: "#fdfdfc", casing: "#e8e8e6" },
      },
      rail: "#d0d0ce",
      boundary: "#b4b4b2",
      labels: {
        place: { text: "#5c5c5c", halo: "#ffffff" },
        road: { text: "#8a8a8a", halo: "#ffffff" },
        water: { text: "#8aa0ad", halo: "#f2f5f7" },
        park: { text: "#8a9a82", halo: "#ffffff" },
        boundary: { text: "#9a9a9a", halo: "#ffffff" },
      },
    },
  },

  // OpenStreetMap Carto — the classic osm.org look: beige land, saturated
  // greens, pink motorways, orange/yellow major roads.
  {
    id: "osm",
    name: "OSM",
    description: "Phong cách OpenStreetMap — đường phân màu theo cấp",
    tone: "light",
    base: LIGHT,
    colors: {
      land: "#f2efe9",
      water: "#aad3df",
      wood: "#add19e",
      park: "#c8facc",
      grass: "#cdebb0",
      residential: "#e0dfdf",
      cemetery: "#aacbaf",
      hospital: "#ffffe5",
      school: "#ffffe5",
      aeroway: "#e9e7e2",
      building: "#d9d0c9",
      buildingOutline: "#c4b6ab",
      roads: {
        motorway: { fill: "#e892a2", casing: "#c24e6b" },
        trunk: { fill: "#f9b29c", casing: "#c84e2f" },
        primary: { fill: "#fcd6a4", casing: "#a06b00" },
        secondary: { fill: "#f7fabf", casing: "#707d05" },
        tertiary: { fill: "#ffffff", casing: "#8f8f8f" },
        minor: { fill: "#ffffff", casing: "#bbbbbb" },
        path: { fill: "#fa8072", casing: "#efe9e2" },
      },
      rail: "#999999",
      boundary: "#8d618b",
      labels: {
        place: { text: "#222222", halo: "#ffffff" },
        road: { text: "#333333", halo: "#ffffff" },
        water: { text: "#4d80b3", halo: "#ffffff" },
        park: { text: "#297d2f", halo: "#ffffff" },
        boundary: { text: "#8d618b", halo: "#ffffff" },
      },
    },
  },

  // National Geographic-style vintage — parchment land, olive greens, sea
  // green water, burnt-orange highways. Good for print and storytelling.
  {
    id: "vintage",
    name: "Vintage",
    description: "Cổ điển kiểu National Geographic — giấy cũ, tông nâu",
    tone: "light",
    base: LIGHT,
    colors: {
      land: "#efe5cf",
      water: "#a9c4c0",
      wood: "#c3cd9d",
      park: "#cbd4a6",
      grass: "#d5dbb3",
      residential: "#e7dcc2",
      cemetery: "#cfd2ac",
      hospital: "#ecd9c8",
      school: "#eadfc4",
      aeroway: "#e3d9c4",
      building: "#dccbb0",
      buildingOutline: "#c8b393",
      roads: {
        motorway: { fill: "#d98a5b", casing: "#a9623b" },
        trunk: { fill: "#e3b06f", casing: "#b9854a" },
        primary: { fill: "#f2dfb4", casing: "#c4a87e" },
        secondary: { fill: "#f7ebcf", casing: "#cdb791" },
        tertiary: { fill: "#faf2df", casing: "#d3c19f" },
        minor: { fill: "#faf3e3", casing: "#d8c8a8" },
        path: { fill: "#f6ecd6", casing: "#d8c8a8" },
      },
      rail: "#a08e74",
      boundary: "#9a7f62",
      labels: {
        place: { text: "#4e3b2a", halo: "#f5ecd9" },
        road: { text: "#6b5844", halo: "#f7efdd" },
        water: { text: "#3f6b6a", halo: "#e2ebe6" },
        park: { text: "#5e6b33", halo: "#f2ead5" },
        boundary: { text: "#7d654c", halo: "#f2ead5" },
      },
    },
  },

  // Carto Dark Matter — near-black neutral canvas, roads in quiet greys.
  {
    id: "dark-matter",
    name: "Dark Matter",
    description: "Phong cách Carto Dark Matter — tối trung tính",
    tone: "dark",
    base: DARK,
    colors: {
      land: "#1b1c1e",
      water: "#0e1013",
      wood: "#1f2520",
      park: "#202722",
      grass: "#212622",
      residential: "#202124",
      cemetery: "#212422",
      hospital: "#262224",
      school: "#252320",
      aeroway: "#232427",
      building: "#26272a",
      buildingOutline: "#2f3034",
      roads: {
        motorway: { fill: "#4a4b4f", casing: "#1b1c1e" },
        trunk: { fill: "#434448", casing: "#1b1c1e" },
        primary: { fill: "#3b3c40", casing: "#1b1c1e" },
        secondary: { fill: "#35363a", casing: "#1b1c1e" },
        tertiary: { fill: "#313236", casing: "#1b1c1e" },
        minor: { fill: "#2c2d31", casing: "#1b1c1e" },
        path: { fill: "#29292d", casing: "#1b1c1e" },
      },
      rail: "#3a3b3f",
      boundary: "#55575c",
      labels: {
        place: { text: "#b4b6ba", halo: "#111214" },
        road: { text: "#8b8d92", halo: "#141517" },
        water: { text: "#5f6f80", halo: "#0e1013" },
        park: { text: "#6f8273", halo: "#141517" },
        boundary: { text: "#7c7f85", halo: "#141517" },
      },
    },
  },

  // Google Maps night — slate-blue land, deep navy water, warm brown
  // highways and orange-tinted place names.
  {
    id: "night",
    name: "Night",
    description: "Phong cách Google Maps ban đêm — xanh than, đường nâu ấm",
    tone: "dark",
    base: DARK,
    colors: {
      land: "#212a37",
      water: "#17263c",
      wood: "#1f3b36",
      park: "#263c3f",
      grass: "#243a39",
      residential: "#242f3e",
      cemetery: "#253638",
      hospital: "#2d2a3a",
      school: "#2b2e3a",
      aeroway: "#28313f",
      building: "#2a3445",
      buildingOutline: "#334055",
      roads: {
        motorway: { fill: "#746855", casing: "#1f2835" },
        trunk: { fill: "#5f574a", casing: "#1f2835" },
        primary: { fill: "#4a5262", casing: "#212a37" },
        secondary: { fill: "#414a59", casing: "#212a37" },
        tertiary: { fill: "#3a4352", casing: "#212a37" },
        minor: { fill: "#343d4c", casing: "#212a37" },
        path: { fill: "#2f3846", casing: "#212a37" },
      },
      rail: "#4b5465",
      boundary: "#5d6b80",
      labels: {
        place: { text: "#d59563", halo: "#17263c" },
        road: { text: "#9ca5b3", halo: "#1b2330" },
        water: { text: "#6b7f99", halo: "#17263c" },
        park: { text: "#6b9a76", halo: "#1b2330" },
        boundary: { text: "#8a96a8", halo: "#1b2330" },
      },
    },
  },

  // Night navigation (in-car) — deep blue-black canvas with bright amber
  // highways and light-grey streets so routes read at a glance.
  {
    id: "navigation",
    name: "Navigation",
    description: "Dẫn đường ban đêm — nền xanh đậm, cao tốc nổi bật",
    tone: "dark",
    base: DARK,
    colors: {
      land: "#151d2b",
      water: "#0a1322",
      wood: "#16292a",
      park: "#182d2c",
      grass: "#172a2b",
      residential: "#19222f",
      cemetery: "#182728",
      hospital: "#211f2d",
      school: "#1f2230",
      aeroway: "#1b2433",
      building: "#1f2a3b",
      buildingOutline: "#283548",
      roads: {
        motorway: { fill: "#f2a64a", casing: "#151d2b" },
        trunk: { fill: "#d9b26a", casing: "#151d2b" },
        primary: { fill: "#7a8aa3", casing: "#151d2b" },
        secondary: { fill: "#55627a", casing: "#151d2b" },
        tertiary: { fill: "#3a4558", casing: "#151d2b" },
        minor: { fill: "#2a3343", casing: "#151d2b" },
        path: { fill: "#232b39", casing: "#151d2b" },
      },
      rail: "#3d4759",
      boundary: "#56637a",
      labels: {
        place: { text: "#e4e9f0", halo: "#0d1420" },
        road: { text: "#b3bccb", halo: "#0d1420" },
        water: { text: "#5c7a9e", halo: "#0a1322" },
        park: { text: "#6f9d8a", halo: "#0d1420" },
        boundary: { text: "#8390a6", halo: "#0d1420" },
      },
    },
  },
];

/** Swatch strip for the picker chip: land → building → park → road → water. */
export function paletteSwatch(p: ColorPalette): string[] {
  const c = p.colors;
  if (!c) return p.swatch ?? [];
  return [c.land, c.building, c.park, c.roads.motorway.fill, c.water];
}

// ── Theme application ──────────────────────────────────────────────────────

type Paint = Record<string, unknown>;

type RoadTierName = keyof PaletteColors["roads"];

/** Road / bridge / tunnel line layer → its tier and whether it is a casing.
 *  Construction, metro (`_MT`) and the special `uncor` variants keep the base
 *  style's look. */
function roadTier(id: string): { tier: RoadTierName; casing: boolean } | null {
  const m = id.match(/^(road|bridge|tunnel)_(motorway|trunk|primary|secondary|tertiary|minor|path)(_link)?(_casing)?$/);
  if (!m) return null;
  return { tier: m[2] as RoadTierName, casing: !!m[4] };
}

function fillColorFor(id: string, c: PaletteColors): string | null {
  if (id === "island_polygon") return c.land;
  if (id === "ocean_planet" || id === "water_polygon") return c.water;
  if (id === "landcover_wood") return c.wood;
  if (id === "landcover_park" || id === "landuse_theme_park") return c.park;
  if (id === "landcover_grass") return c.grass;
  if (id === "landuse_residential") return c.residential;
  if (id === "landuse_cemetery") return c.cemetery;
  if (id === "landuse_hospital") return c.hospital;
  if (id === "landuse_school") return c.school;
  if (id === "aeroway_fill") return c.aeroway;
  if (/^building/.test(id)) return c.building;
  return null;
}

function lineColorFor(id: string, c: PaletteColors): string | null {
  if (id === "water_line") return c.water;
  const road = roadTier(id);
  if (road) {
    const tier = c.roads[road.tier];
    return road.casing ? tier.casing : tier.fill;
  }
  if (id === "road_rail" || id === "road_rail_casing") return c.rail;
  if (id === "road_ferry") return c.labels.water.text;
  if (/^boundary_/.test(id)) return c.boundary;
  return null;
}

function labelColorsFor(id: string, c: PaletteColors): LabelColors | null {
  // POI icons + their labels keep Vietmap's category colours.
  if (/^poi/.test(id) || /shield$/.test(id)) return null;
  if (/^(water_name|water_point|waterway_name)$/.test(id) || id === "road_ferry_label") return c.labels.water;
  if (id === "park_label") return c.labels.park;
  if (/^boundary_/.test(id)) return c.labels.boundary;
  if (/^road_.*_label$/.test(id)) return c.labels.road;
  if (/^(place_|country_)/.test(id)) return c.labels.place;
  return null;
}

/**
 * Recolour a base style's layers with a theme. Only paint colours change;
 * layer order, filters, widths and zoom behaviour stay as Vietmap tuned them.
 * Expressions are replaced by the flat theme colour (themes are flat by
 * design), opacity expressions are left alone.
 */
export function applyPaletteTheme(layers: LayerSpecification[], palette: ColorPalette): LayerSpecification[] {
  const c = palette.colors;
  if (!c) return layers;
  return layers.map((layer) => {
    const paint: Paint = { ...((layer as { paint?: Paint }).paint || {}) };
    let changed = false;
    const set = (key: string, value: string | number) => {
      paint[key] = value;
      changed = true;
    };

    switch (layer.type) {
      case "background":
        set("background-color", c.land);
        break;
      case "fill": {
        const color = fillColorFor(layer.id, c);
        if (color) {
          set("fill-color", color);
          if (/^building/.test(layer.id)) set("fill-outline-color", c.buildingOutline);
          else if ("fill-outline-color" in paint) set("fill-outline-color", color);
        }
        break;
      }
      case "fill-extrusion":
        if (/^building/.test(layer.id)) set("fill-extrusion-color", c.building);
        break;
      case "line": {
        const color = lineColorFor(layer.id, c);
        if (color) set("line-color", color);
        break;
      }
      case "symbol": {
        const label = labelColorsFor(layer.id, c);
        if (label) {
          set("text-color", label.text);
          set("text-halo-color", label.halo);
          if (!("text-halo-width" in paint)) set("text-halo-width", 1.2);
        }
        break;
      }
    }

    return changed ? ({ ...layer, paint } as LayerSpecification) : layer;
  });
}
